// Production Firefly renderer — glowing bug near an anchor with ambient
// hover, highlight cycles, V9 reduced motion. Dev Lab imports this module.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyFireflySpeedMultiplier,
  getFireflyIntentParams,
  type FireflyIntent,
} from "./fireflyIntents";
import {
  FIREFLY_REDUCED_CROSSFADE_MS,
  fireflyPulseKeyframesCss,
  flickerPhaseMs,
  hoverDriftStyle,
  pulseAnimationStyle,
  scaledHighlightRemoteDwellMs,
  scaledReducedHighlightDwellMs,
} from "./fireflyMotion";
import { FireflyBugSvg, fireflyBugRenderSizePx } from "./FireflyBugSvg";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";
import { useFireflyMotionSpeedMultiplier } from "./useFireflyMotionSpeedMultiplier";

export type FireflyProps = {
  intent: FireflyIntent;
  anchorX: number;
  anchorY: number;
  highlightGeneration?: number;
  highlightX?: number;
  highlightY?: number;
  ambientHover?: boolean;
  /** Lab override — force V9 expression without OS setting. */
  forceReducedMotion?: boolean;
  /** V5 — true when idle at anchor and ready for dwell countdown. */
  onAnchorDwellReadyChange?: (ready: boolean) => void;
  /** Panel "Stop Blinking!" — static red: no jitter, pulse, or hover drift. */
  blinkingSuppressed?: boolean;
  /** Drag-chase flight — smooth pursuit; no jitter/heartbeat, hover ok. */
  dragChasing?: boolean;
  /** Increment to abort in-flight travel and fade back to anchor. */
  motionResetGeneration?: number;
};

type MotionPhase =
  | "settled"
  | "fading-out"
  | "dark"
  | "fading-in"
  | "highlight-at-remote"
  | "return-to-anchor";

export function Firefly({
  intent,
  anchorX,
  anchorY,
  highlightGeneration = 0,
  highlightX = anchorX,
  highlightY = anchorY,
  ambientHover = true,
  forceReducedMotion = false,
  onAnchorDwellReadyChange,
  blinkingSuppressed = false,
  dragChasing = false,
  motionResetGeneration = 0,
}: FireflyProps) {
  const systemReducedMotion = usePrefersReducedMotion();
  const [motionSpeedMultiplier] = useFireflyMotionSpeedMultiplier();
  const reducedMotion = forceReducedMotion || systemReducedMotion;
  const calmExpression = reducedMotion || blinkingSuppressed;
  const stillExpression = calmExpression || dragChasing;

  const params = useMemo(() => {
    const base = getFireflyIntentParams(intent);
    if (!base) return null;
    return applyFireflySpeedMultiplier(base, motionSpeedMultiplier);
  }, [intent, motionSpeedMultiplier]);

  const [baseX, setBaseX] = useState(anchorX);
  const [baseY, setBaseY] = useState(anchorY);
  const [phase, setPhase] = useState<MotionPhase>("settled");
  const [jitterX, setJitterX] = useState(0);
  const [jitterY, setJitterY] = useState(0);
  const [teachingEntryPlaying, setTeachingEntryPlaying] = useState(false);

  const phaseTimersRef = useRef<number[]>([]);
  const lastAnchorRef = useRef({ x: anchorX, y: anchorY });
  const anchorRef = useRef({ x: anchorX, y: anchorY });
  const lastHighlightGenRef = useRef(0);
  const lastMotionResetGenRef = useRef(0);

  useEffect(() => {
    anchorRef.current = { x: anchorX, y: anchorY };
  }, [anchorX, anchorY]);

  const clearPhaseTimers = () => {
    phaseTimersRef.current.forEach((id) => window.clearTimeout(id));
    phaseTimersRef.current = [];
  };

  const schedule = (fn: () => void, ms: number) => {
    phaseTimersRef.current.push(window.setTimeout(fn, ms));
  };

  const runFlickerTo = (
    x: number,
    y: number,
    onComplete: () => void,
    flickerMs: number,
  ) => {
    const { fadeOutMs, darkGapMs } = flickerPhaseMs(flickerMs);
    setPhase("fading-out");
    schedule(() => {
      setPhase("dark");
      setBaseX(x);
      setBaseY(y);
    }, fadeOutMs);
    schedule(() => setPhase("fading-in"), fadeOutMs + darkGapMs);
    schedule(() => {
      setPhase("settled");
      onComplete();
    }, flickerMs);
  };

  const runSoftCrossfadeTo = (
    x: number,
    y: number,
    onComplete: () => void,
  ) => {
    const legMs = FIREFLY_REDUCED_CROSSFADE_MS;
    setPhase("fading-out");
    schedule(() => {
      setBaseX(x);
      setBaseY(y);
      setPhase("fading-in");
    }, legMs);
    schedule(() => {
      setPhase("settled");
      onComplete();
    }, legMs * 2);
  };

  const runMoveTo = (
    x: number,
    y: number,
    onComplete: () => void,
    flickerMs: number,
  ) => {
    if (reducedMotion) {
      runSoftCrossfadeTo(x, y, onComplete);
    } else {
      runFlickerTo(x, y, onComplete, flickerMs);
    }
  };

  const resetToAnchorInstant = useCallback(() => {
    clearPhaseTimers();
    setBaseX(anchorRef.current.x);
    setBaseY(anchorRef.current.y);
    setPhase("settled");
    setJitterX(0);
    setJitterY(0);
  }, []);

  useEffect(() => {
    if (
      anchorX === lastAnchorRef.current.x &&
      anchorY === lastAnchorRef.current.y
    ) {
      return;
    }
    lastAnchorRef.current = { x: anchorX, y: anchorY };
    if (phase === "settled" || phase === "highlight-at-remote") {
      setBaseX(anchorX);
      setBaseY(anchorY);
    }
  }, [anchorX, anchorY, phase]);

  useEffect(() => {
    if (reducedMotion || intent !== "teaching" || !params?.entryPulseMs) {
      setTeachingEntryPlaying(false);
      return;
    }
    setTeachingEntryPlaying(true);
    const id = window.setTimeout(
      () => setTeachingEntryPlaying(false),
      params.entryPulseMs,
    );
    return () => window.clearTimeout(id);
  }, [intent, params?.entryPulseMs, reducedMotion]);

  useEffect(() => {
    if (!params || highlightGeneration === 0) return;
    if (highlightGeneration === lastHighlightGenRef.current) return;
    lastHighlightGenRef.current = highlightGeneration;

    clearPhaseTimers();

    const remoteDwellMs = reducedMotion
      ? scaledReducedHighlightDwellMs(motionSpeedMultiplier)
      : scaledHighlightRemoteDwellMs(motionSpeedMultiplier);

    runMoveTo(
      highlightX,
      highlightY,
      () => {
        setPhase("highlight-at-remote");
        schedule(() => {
          const home = anchorRef.current;
          runMoveTo(home.x, home.y, () => {}, params.flickerDurationMs);
        }, remoteDwellMs);
      },
      params.flickerDurationMs,
    );

    return clearPhaseTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- anchor read via ref at return time
  }, [
    highlightGeneration,
    highlightX,
    highlightY,
    params,
    reducedMotion,
    motionSpeedMultiplier,
  ]);

  useEffect(() => {
    if (motionResetGeneration === 0) return;
    if (motionResetGeneration === lastMotionResetGenRef.current) return;
    lastMotionResetGenRef.current = motionResetGeneration;
    resetToAnchorInstant();
  }, [motionResetGeneration, resetToAnchorInstant]);

  useEffect(() => {
    if (
      stillExpression ||
      !params?.jitterIntervalMs ||
      !params.jitterRadiusPx
    ) {
      setJitterX(0);
      setJitterY(0);
      return;
    }
    const r = params.jitterRadiusPx;
    const tick = () => {
      if (phase !== "settled") return;
      setJitterX((Math.random() * 2 - 1) * r);
      setJitterY((Math.random() * 2 - 1) * r);
    };
    tick();
    const id = window.setInterval(tick, params.jitterIntervalMs);
    return () => window.clearInterval(id);
  }, [
    params?.jitterIntervalMs,
    params?.jitterRadiusPx,
    phase,
    stillExpression,
  ]);

  const anchorDwellReady = phase === "settled";

  useEffect(() => {
    onAnchorDwellReadyChange?.(anchorDwellReady);
  }, [anchorDwellReady, onAnchorDwellReadyChange]);

  if (!params) {
    return (
      <div
        title={`Firefly: ${intent} (visual not yet specced)`}
        style={{
          position: "absolute",
          left: anchorX - 4,
          top: anchorY - 4,
          width: 8,
          height: 8,
          borderRadius: 9999,
          background: "rgba(148, 163, 184, 0.6)",
          border: "1px dashed rgba(100, 116, 139, 0.8)",
        }}
      />
    );
  }

  const isHidden = phase === "fading-out" || phase === "dark";
  const flickerMs = params.flickerDurationMs;
  const opacityTransitionMs = reducedMotion
    ? FIREFLY_REDUCED_CROSSFADE_MS
    : phase === "fading-out"
      ? flickerPhaseMs(flickerMs).fadeOutMs
      : phase === "fading-in"
        ? flickerPhaseMs(flickerMs).fadeInMs
        : 0;

  const hover =
    ambientHover && !calmExpression ? hoverDriftStyle(params) : null;
  const pulseAnim = stillExpression
    ? undefined
    : pulseAnimationStyle(params, teachingEntryPlaying);
  const renderX = baseX + (stillExpression ? 0 : jitterX);
  const renderY = baseY + (stillExpression ? 0 : jitterY);
  const bugSizePx = fireflyBugRenderSizePx(params.bodyDiameterPx);
  const bugLeft = params.glowDiameterPx / 2 - bugSizePx / 2;
  const bugTop = params.glowDiameterPx / 2 - bugSizePx / 2;
  const settledOpacity = calmExpression ? 0.92 : 1;
  const motionLayerKey = `${intent}-${motionSpeedMultiplier}-${params.pulseDurationMs}-${params.hoverDurationMs}`;

  return (
    <>
      {!stillExpression ? <style>{fireflyPulseKeyframesCss()}</style> : null}
      <div
        key={motionLayerKey}
        aria-hidden
        style={{
          position: "absolute",
          left: renderX - params.glowDiameterPx / 2,
          top: renderY - params.glowDiameterPx / 2,
          width: params.glowDiameterPx,
          height: params.glowDiameterPx,
          opacity: isHidden ? 0 : settledOpacity,
          transition: `opacity ${opacityTransitionMs}ms ease-in-out`,
          pointerEvents: "none",
          ...(hover?.cssVars ?? {}),
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            animation: hover?.animation,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              animation: pulseAnim,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 9999,
                background: params.glowColor,
                filter: `blur(${Math.round(params.glowDiameterPx / 3)}px)`,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: bugLeft,
                top: bugTop,
                width: bugSizePx,
                height: bugSizePx,
              }}
            >
              <FireflyBugSvg sizePx={bugSizePx} fillColor={params.glowColor} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
